/**
 * The account screen's data access, as one narrow interface.
 *
 * Every method is declared on `AccountApi` and the screen takes that interface
 * rather than reaching for `api` itself. Two reasons, both practical:
 *
 *  • the screen becomes testable in jsdom without intercepting HTTP, so the
 *    tests assert what the screen DOES with an answer rather than how axios
 *    was configured;
 *  • the set of endpoints this screen can touch is written down in one place.
 *    A settings page quietly acquiring a tenth backend call is a thing you
 *    want to see in a diff.
 *
 * ── WHERE THESE ENDPOINTS COME FROM ─────────────────────────────────────────
 * Three different owners, deliberately not merged:
 *   `/account/*`         the game's own module — core has none of this.
 *   `/consent*`          the consent module owns the record and its documents.
 * Wrapping both behind one interface is a convenience for the screen, not a
 * claim that the game owns them.
 *
 * ── WHAT IS DELIBERATELY ABSENT ─────────────────────────────────────────────
 * There is no `setCurrency` here, and there must not be. The display currency
 * is owned by `@/engine/layout/currency`, which holds the active value, the
 * device-local copy, the push to `PATCH /me/preferences`, and the subscriber
 * list a footer switcher renders from. A second writer for that one column is
 * how the settings screen and the footer end up disagreeing about what
 * currency the player chose. Locale is the same story, owned by
 * `@/engine/i18n`'s `setLocale`.
 */
import { api } from '@/core/network/api';
import type { FxSnapshot } from './money';
import type { BillingApi, BillingProfileInput } from '@/engine/meta/settings-screen';

/* ══════════════════════════════════════════════════════ account shapes ══ */

export interface AccountProfile {
  readonly id: string;
  readonly email: string;
  readonly username: string | null;
  readonly emailVerified: boolean;
  readonly role: string;
  readonly createdAt: string;
  readonly level: number;
  readonly xp: number;
}

export interface AccountPreferences {
  /** Core's identifier shape (`en_GB`), not our `en`. Null = never chosen. */
  readonly locale: string | null;
  readonly currency: string | null;
}

/** In-game currency. Integers, no ISO code, no cash value. */
export interface GameBalances {
  readonly soft: number;
  readonly premium: number;
}

export interface AccountOverview {
  readonly profile: AccountProfile;
  readonly preferences: AccountPreferences;
  readonly balances: GameBalances;
  readonly fx: FxSnapshot;
  readonly supportedLocales: readonly string[];
}

export interface PasswordChangeResult {
  readonly changed: true;
  readonly sessionsRevoked: number;
}

export interface AccountDeletionResult {
  readonly deleted: true;
  readonly userId: string;
  readonly consentPseudonym: string;
  readonly consentRecordsRedacted: number;
  readonly consentRecordsRetained: number;
  readonly deletedAt: string;
}

/* ══════════════════════════════════════════════════════ consent shapes ══ */

export type ConsentPurpose =
  | 'TERMS_OF_SERVICE'
  | 'PRIVACY_POLICY'
  | 'MARKETING_EMAIL'
  | 'ANALYTICS';

export type ConsentStatus = 'NEVER_GIVEN' | 'GRANTED' | 'WITHDRAWN' | 'SUPERSEDED';

/**
 * There is deliberately no client-side list of which purposes are required.
 * `ConsentStateItem.required` says so per item, and a second copy here would
 * be a second source of truth for a classification only the server can change.
 */
export interface LegalDocumentRef {
  readonly id: string;
  readonly slug: string;
  readonly version: number;
  readonly purpose: ConsentPurpose | null;
  readonly title: string;
  readonly contentHash: string;
  readonly locale: string | null;
  readonly effectiveFrom: string;
  readonly supersededAt: string | null;
  readonly publishedAt: string;
}

export interface ConsentDocumentStamp {
  readonly versionId: string;
  readonly slug: string;
  readonly version: number;
  readonly contentHash: string;
}

export interface ConsentStateItem {
  readonly purpose: ConsentPurpose;
  readonly required: boolean;
  readonly status: ConsentStatus;
  readonly satisfied: boolean;
  readonly document: LegalDocumentRef | null;
  readonly acceptedDocument: ConsentDocumentStamp | null;
  readonly lastActionAt: string | null;
  /** Required, but the operator has published nothing. Their fault, not the player's. */
  readonly documentMissing: boolean;
}

export interface ConsentRequirementReport {
  readonly items: readonly ConsentStateItem[];
  readonly outstanding: readonly ConsentPurpose[];
  readonly blocked: boolean;
  readonly unpublished: readonly ConsentPurpose[];
}

export interface ConsentHistoryEntry {
  readonly id: string;
  /** A BigInt on the server; a decimal STRING on the wire. Never a number. */
  readonly seq: string;
  readonly purpose: ConsentPurpose;
  readonly action: 'GRANTED' | 'WITHDRAWN';
  readonly document: ConsentDocumentStamp | null;
  readonly documentTitle: string | null;
  readonly ipAddress: string | null;
  readonly userAgent: string | null;
  readonly source: string;
  readonly locale: string | null;
  readonly pseudonymisedAt: string | null;
  readonly recordedAt: string;
}

export interface ConsentReceipt extends ConsentHistoryEntry {
  readonly purposeRequired: boolean;
  /** `false` when nothing changed — clicking twice is not a second consent. */
  readonly changed: boolean;
}

/* ═════════════════════════════════════════════════ transaction shapes ══ */

/**
 * ONE PURCHASE, MIRRORED FROM `GET /api/shop/transactions`.
 *
 * The shape is copied field for field from `TransactionView` in
 * `backend/src/game/payments/invoice-history.controller.ts` — the file that
 * serves it — because a client type invented from a screenshot of a response
 * is how a history quietly starts rendering `undefined`. Every optional here is
 * optional ON THE SERVER for a stated reason, not because it "might" be absent:
 *
 *   • `sku`/`productName` are null when the payment names a sku this build's
 *     catalogue no longer carries. The row still renders; the amount and the
 *     date are what the player needs.
 *   • `invoice` is null when the document failed to render. That is precisely
 *     the row worth showing, so it is a nullable field and not a filter.
 *   • `granted` is empty for a payment that never reached fulfilment.
 */
export interface TransactionGrant {
  /** `SOFT` | `PREMIUM` — in-game currency. NEVER formatted as money. */
  readonly currency: string;
  readonly amount: number;
}

export interface TransactionInvoice {
  readonly number: string;
  /** `PENDING` | `PAID` | `CANCELLED`. */
  readonly status: string;
  /** Minor units of `currency`. Never divided on the client. */
  readonly totalCents: number;
  readonly currency: string;
  /** Appended to the API base by the screen — never assembled from parts. */
  readonly downloadPath: string;
}

export interface Transaction {
  readonly id: string;
  readonly type: 'purchase' | 'withdrawal';
  readonly createdAt: string;
  /** `PENDING` | `COMPLETED` | `FAILED` | `REFUNDED` — core's `PaymentStatus`. */
  readonly status: string;
  readonly sku: string | null;
  readonly productName: string | null;
  /** Minor units of `currency`. Positive for purchases, negative for withdrawals. */
  readonly amountMinor: number;
  readonly currency: string;
  readonly provider: string;
  /** True = no money moved. The screen must say so, unmissably. */
  readonly simulated: boolean;
  readonly granted: readonly TransactionGrant[];
  readonly fulfilledAt: string | null;
  readonly invoice: TransactionInvoice | null;
}

export interface TransactionPage {
  readonly items: readonly Transaction[];
  readonly total: number;
}

/* ═══════════════════════════════════════════════════════ the interface ══ */

export interface AccountApi {
  overview(): Promise<AccountOverview>;
  changePassword(currentPassword: string, newPassword: string): Promise<PasswordChangeResult>;
  consentState(): Promise<ConsentRequirementReport>;
  consentHistory(limit?: number): Promise<readonly ConsentHistoryEntry[]>;
  grantConsent(purpose: ConsentPurpose, against: LegalDocumentRef | null): Promise<ConsentReceipt>;
  withdrawConsent(purpose: ConsentPurpose): Promise<ConsentReceipt>;
  transactions(): Promise<TransactionPage>;
  deleteAccount(password: string, confirmation: string, reason?: string): Promise<AccountDeletionResult>;
}

/* ═════════════════════════════════════════════════════ the HTTP client ══ */

export const httpAccountApi: AccountApi = {
  overview: () => api.get<AccountOverview>('/account/overview'),

  changePassword: (currentPassword, newPassword) =>
    api.post<PasswordChangeResult>('/account/password', { currentPassword, newPassword }),

  consentState: () => api.get<ConsentRequirementReport>('/consent/required'),

  consentHistory: (limit) =>
    api
      .get<{ history: ConsentHistoryEntry[] }>(`/consent/me${limit === undefined ? '' : `?limit=${limit}`}`)
      .then((r) => r.history),

  /**
   * Grant one consent, PINNED to the document version the player was shown.
   *
   * `documentVersion` and `documentHash` are sent whenever we have them, so a
   * publish landing between render and click comes back as a 409 rather than
   * being recorded as agreement to text nobody read.
   *
   * They are OMITTED when the purpose has no document of its own. That is not
   * laziness: the server resolves marketing and analytics against the privacy
   * NOTICE, while `GET /consent/required` reports `document: null` for them.
   * Inventing a version here would pin the record to a document the state
   * endpoint never named. (See the note in the report — the asymmetry is the
   * server's, and the safe side of it is to send nothing.)
   */
  grantConsent: (purpose, against) =>
    api.post<ConsentReceipt>('/consent', {
      purpose,
      ...(against === null
        ? {}
        : { documentVersion: against.version, documentHash: against.contentHash }),
    }),

  withdrawConsent: (purpose) => api.del<ConsentReceipt>(`/consent/${purpose}`),

  transactions: () => api.get<TransactionPage>('/shop/transactions'),

  deleteAccount: (password, confirmation, reason) =>
    api.post<AccountDeletionResult>('/account/deletion', {
      password,
      confirmation,
      ...(reason === undefined || reason === '' ? {} : { reason }),
    }),
};

/* ═════════════════════════════════════════════════════ the billing client ══ */

/**
 * `GET`/`PUT /api/account/billing` — the settings screen's billing form.
 *
 * Implements `BillingApi`, whose shape is declared beside the form in
 * `engine/meta/settings-screen.ts` (the seam it is injected into). It lives
 * here, beside `httpAccountApi`, because this file owns the `/account/*`
 * surface — and it is INJECTED into the settings screen from `engine/meta/
 * screens.ts` rather than defaulted inside `renderSettingsScreen`, so a caller
 * that mounts the screen without it still gets the read-only form.
 *
 * `load` returns `null` when the player has never filled the form in: the
 * server sends JSON `null`, not an empty object, and the form reads that as
 * "never chosen" rather than "every field blank". `save` is a `PUT` — the form
 * submits every field, so a cleared line is a cleared column.
 */
export const httpBillingApi: BillingApi = {
  load: () => api.get<BillingProfileInput | null>('/account/billing'),
  save: (profile) => api.put<BillingProfileInput>('/account/billing', profile),
};

/** The literal word `POST /account/deletion` requires. Checked server-side too. */
export const DELETION_CONFIRMATION_WORD = 'DELETE';

/**
 * Path (to be appended to `API_BASE`, never assembled from parts) that
 * downloads ONE pinned legal document version as a file.
 *
 * The version id comes from a `ConsentDocumentStamp.versionId` the consent
 * state already carries — so the file the player downloads is the exact text
 * they accepted, not the current head. Served by
 * `backend/src/game/consent/consent.controller.ts` at
 * `GET /consent/documents/version/:versionId/download`. Public content, so a
 * plain authenticated link is all the screen needs.
 */
export function legalDocumentDownloadPath(versionId: string): string {
  return `/consent/documents/version/${encodeURIComponent(versionId)}/download`;
}
